import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import axios from 'axios';
import { Check, Edit2, Info, Save, X } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { tempData } from './tempData';
import { UserContext } from '../../../context/authContext';
import { DocumentContext } from '../../../context/documentContext';
import clsx from 'clsx';
import { PdfScrollViewer } from '../pdfViewer/pdfScrollViewer';
import {
  findAllMatching,
  findAndMarkInNode
} from '../pdfViewer/findAndHighlight';
import QuestionsSpreadsheetViewer from './spreadsheetViewer';
import logger from '../../../helpers/Logger';
import { ErrorBoundaryCustom } from '../errorScreen/ErrorBoundaryCustom';
import {
  DOCTYPE,
  documentTableItem,
  IRFP_Manifest,
  IRFP_PreviousAnswer,
  IRFP_Questions,
  ISelectedCell
} from '../../../lib/models/interfaces';
import { OptionDefinition } from '@cloudscape-design/components/internal/components/option/interfaces';
import { Button, Modal } from '@cloudscape-design/components';
import { DocumentFilter } from '../Chat/documentFilter';
import RFPAnswerCard from '../rfpUploader/RFPAnswerCard';
import { hash } from '../rfpUploader/helpers';

interface QuestionItem {
  question: string;
  answer: string;
  questionCell: string;
  answerCell: string;
  ignored?: boolean;
  sheetKey: string;
  userInput?: boolean;
  isOriginal?: boolean;
  sourceQuestion?: Record<string, any>;
  _uuid?: string;
}

interface EditingCell {
  index: number;
  field: 'question' | 'answer';
}

interface QuestionnaireJson {
  $meta_data?: {
    documentType?: string;
    published?: boolean;
  };
  [key: string]: any;
}

type ManifestWithOriginal = IRFP_Manifest & {
  original?: any[];
  searchDocs?: Record<string, any>;
};

const QUESTION_TABLE_ACCENT_COLOR = '#62d29e';
const QUESTIONNAIRE_SAVE_PRESIGNED_TYPE = 'putQuestionnaire';
const QUESTIONNARE_RESAVE = 'uploadRFPManifestResave';

const hexToRgb = (
  hexColor: string
): { red: number; green: number; blue: number } => {
  const sanitizedHex = hexColor.replace('#', '');
  const normalizedHex =
    sanitizedHex.length === 3
      ? sanitizedHex
          .split('')
          .map((char) => `${char}${char}`)
          .join('')
      : sanitizedHex;
  const hexNumber = Number.parseInt(normalizedHex, 16);

  return {
    red: (hexNumber >> 16) & 255,
    green: (hexNumber >> 8) & 255,
    blue: hexNumber & 255
  };
};

const {
  red: accentRed,
  green: accentGreen,
  blue: accentBlue
} = hexToRgb(QUESTION_TABLE_ACCENT_COLOR);

const questionTableAccentVars = {
  '--qt-accent-color': QUESTION_TABLE_ACCENT_COLOR,
  '--qt-accent-soft': `rgba(${accentRed}, ${accentGreen}, ${accentBlue}, 0.12)`,
  '--qt-accent-line': `rgba(${accentRed}, ${accentGreen}, ${accentBlue}, 0.45)`
} as React.CSSProperties;

const mapRowToQuestionRecord = (row: QuestionItem): Record<string, any> => {
  const base = row.sourceQuestion ? { ...row.sourceQuestion } : {};
  return {
    ...base,
    question: row.question,
    answer: row.answer,
    questionCell: row.questionCell,
    answerCell: row.answerCell,
    ignored: Boolean(row.ignored)
  };
};

const getPdfPageKey = (pageValue: any, index: number): string =>
  String(
    pageValue?.pageNo ??
      pageValue?.pageNumber ??
      pageValue?.page ??
      pageValue?.pageKey ??
      pageValue?.pageIndex ??
      index + 1
  );

const getViewerFileTypeFromDocument = (
  documentRecord: documentTableItem | null
): string | null => {
  if (!documentRecord) {
    return null;
  }
  const extension =
    documentRecord.fileExt ??
    documentRecord.documentFileName?.split('.').at(-1) ??
    '';
  const normalizedExtension = extension.toLowerCase();
  if (normalizedExtension === 'xlsx' || normalizedExtension === 'xls') {
    return 'sheet';
  }
  if (normalizedExtension === 'docx' || normalizedExtension === 'pdf') {
    return 'pdf';
  }
  return null;
};

const computeManifestSha = async (
  manifest: ManifestWithOriginal | null
): Promise<string> => {
  if (!manifest) {
    return '';
  }
  try {
    return await hash(manifest);
  } catch (error) {
    logger.error('error hashing manifest', error);
    return '';
  }
};

const syncDataToQuestionnaireJson = (
  sourceJson: QuestionnaireJson,
  rows: QuestionItem[]
): QuestionnaireJson => {
  const nextJson = structuredClone(sourceJson);
  const groupedRows = rows.reduce(
    (acc, row) => {
      if (!acc[row.sheetKey]) {
        acc[row.sheetKey] = [];
      }
      acc[row.sheetKey].push(row);
      return acc;
    },
    {} as Record<string, QuestionItem[]>
  );
  console.log('Next JSON', nextJson);
  const documentType = nextJson.$meta_data?.documentType;
  const jsonSheetKeys = Object.keys(nextJson).filter(
    (key) => key !== '$meta_data'
  );

  if (documentType === 'pdf') {
    if (Array.isArray(nextJson.pages)) {
      const pages = [...nextJson.pages];
      const pageKeys = pages.map((page: any, index: number) =>
        getPdfPageKey(page, index)
      );
      const pageKeySet = new Set(pageKeys);

      for (let index = 0; index < pages.length; index++) {
        const pageKey = pageKeys[index];
        const pageObject = pages[index] ?? {};
        pages[index] = {
          ...pageObject,
          pageQuestions: (groupedRows[pageKey] ?? []).map(
            mapRowToQuestionRecord
          )
        };
      }

      for (const pageKey of Object.keys(groupedRows)) {
        if (!pageKeySet.has(pageKey)) {
          const parsedPageNo = Number(pageKey);
          pages.push({
            ...(Number.isFinite(parsedPageNo)
              ? { pageNo: parsedPageNo }
              : { pageKey }),
            pageQuestions: groupedRows[pageKey].map(mapRowToQuestionRecord)
          });
        }
      }

      nextJson.pages = pages;
      return nextJson;
    }

    const pageKeys = jsonSheetKeys.filter((key) => {
      if (key === 'pages' || key === 'documentId') {
        return false;
      }
      const pageValue = nextJson[key];
      return (
        pageValue &&
        typeof pageValue === 'object' &&
        Array.isArray(pageValue.pageQuestions)
      );
    });

    for (const pageKey of pageKeys) {
      const pageObject = nextJson[pageKey] ?? {};
      pageObject.pageQuestions = (groupedRows[pageKey] ?? []).map(
        mapRowToQuestionRecord
      );
      nextJson[pageKey] = pageObject;
    }
    for (const pageKey of Object.keys(groupedRows)) {
      if (!nextJson[pageKey]) {
        nextJson[pageKey] = {
          pageQuestions: groupedRows[pageKey].map(mapRowToQuestionRecord)
        };
      }
    }
    return nextJson;
  }

  if (documentType === 'xlsx') {
    for (const sheetKey of jsonSheetKeys) {
      const sheetObject = nextJson[sheetKey] ?? {};
      const pages = Array.isArray(sheetObject.pages)
        ? [...sheetObject.pages]
        : [];
      if (!pages.length) {
        pages.push({});
      }
      const firstPage = pages[0] ?? {};
      firstPage.pageQuestions = (groupedRows[sheetKey] ?? []).map(
        mapRowToQuestionRecord
      );
      pages[0] = firstPage;
      sheetObject.pages = pages;
      nextJson[sheetKey] = sheetObject;
    }
    for (const sheetKey of Object.keys(groupedRows)) {
      if (!nextJson[sheetKey]) {
        nextJson[sheetKey] = {
          pages: [
            {
              pageQuestions: groupedRows[sheetKey].map(mapRowToQuestionRecord)
            }
          ]
        };
      }
    }
    return nextJson;
  }

  return nextJson;
};

const RFP_CITATION_REGEX = /<<cid:[A-Fa-f0-9]{24}>>|\u2039[^\u203a]*\u203a/g;

type ValidationAuditEntry = {
  name: string;
  date: string;
  valid: boolean;
};

const normalizeValidationAuditEntries = (
  validatedBy: unknown,
  fallbackDate: string
): ValidationAuditEntry[] => {
  if (!Array.isArray(validatedBy)) {
    return [];
  }

  return validatedBy.reduce((acc: ValidationAuditEntry[], entry: unknown) => {
    if (typeof entry === 'string' && entry.trim()) {
      acc.push({
        name: entry.trim(),
        date: fallbackDate,
        valid: true
      });
      return acc;
    }

    if (!entry || typeof entry !== 'object') {
      return acc;
    }

    const name =
      typeof (entry as { name?: unknown }).name === 'string'
        ? (entry as { name: string }).name.trim()
        : '';
    if (!name) {
      return acc;
    }

    const date =
      typeof (entry as { date?: unknown }).date === 'string' &&
      (entry as { date: string }).date.trim()
        ? (entry as { date: string }).date
        : fallbackDate;
    const valid =
      typeof (entry as { valid?: unknown }).valid === 'boolean'
        ? (entry as { valid: boolean }).valid
        : true;

    acc.push({
      name,
      date,
      valid
    });
    return acc;
  }, []);
};

const formatValidationDate = (date: string): string => {
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) {
    return date || 'Unknown date';
  }

  return parsedDate.toLocaleString();
};

const getLatestSourceAnswerText = (
  sourceQuestion: Record<string, any>,
  fallbackAnswer: string
): string => {
  if (
    Array.isArray(sourceQuestion.answer) &&
    sourceQuestion.answer.length > 0
  ) {
    for (let index = sourceQuestion.answer.length - 1; index >= 0; index--) {
      const answer = sourceQuestion.answer[index];
      if (typeof answer?.text === 'string' && answer.text.trim()) {
        return answer.text;
      }
    }
  }
  if (
    typeof sourceQuestion.answerText === 'string' &&
    sourceQuestion.answerText.trim()
  ) {
    return sourceQuestion.answerText;
  }
  return fallbackAnswer;
};

const getManifestAnswerText = (
  manifestQuestion: IRFP_Manifest['questions'][number]
): string => {
  if (
    typeof manifestQuestion.answerText === 'string' &&
    manifestQuestion.answerText.trim()
  ) {
    return manifestQuestion.answerText;
  }

  if (
    !Array.isArray(manifestQuestion.answer) ||
    manifestQuestion.answer.length < 1
  ) {
    return '';
  }

  for (let index = manifestQuestion.answer.length - 1; index >= 0; index--) {
    const answer = manifestQuestion.answer[index];
    if (typeof answer?.text === 'string' && answer.text.trim()) {
      return answer.text;
    }
  }

  for (let index = manifestQuestion.answer.length - 1; index >= 0; index--) {
    const answer = manifestQuestion.answer[index];
    if (
      typeof answer?.textInternal === 'string' &&
      answer.textInternal.trim()
    ) {
      return answer.textInternal.replace(RFP_CITATION_REGEX, '').trim();
    }
  }

  return '';
};

const findManifestQuestionIndex = (
  questions: IRFP_Manifest['questions'],
  row: {
    _uuid?: string;
    sheetKey?: string;
    questionCell?: string;
    answerCell?: string;
    question?: string;
    sourceQuestion?: Record<string, any>;
  }
): number => {
  const sourceQuestion = row.sourceQuestion ?? {};
  const rowUuid = row._uuid ?? sourceQuestion._uuid;
  if (rowUuid) {
    const byUuid = questions.findIndex(
      (question) =>
        question?._uuid && String(question._uuid) === String(rowUuid)
    );
    if (byUuid !== -1) {
      return byUuid;
    }
  }

  const rowSheetKey = String(
    row.sheetKey ??
      sourceQuestion.questionSheet ??
      sourceQuestion.answerSheet ??
      ''
  );
  const rowAnswerCell = String(
    row.answerCell ?? sourceQuestion.answerCell ?? ''
  );
  if (rowSheetKey && rowAnswerCell) {
    const byAnswerCell = questions.findIndex(
      (question) =>
        question?.answerSheet === rowSheetKey &&
        question?.answerCell === rowAnswerCell
    );
    if (byAnswerCell !== -1) {
      return byAnswerCell;
    }
  }

  const rowQuestionCell = String(
    row.questionCell ?? sourceQuestion.questionCell ?? ''
  );
  if (rowSheetKey && rowQuestionCell) {
    const byQuestionCell = questions.findIndex(
      (question) =>
        question?.questionSheet === rowSheetKey &&
        question?.questionCell === rowQuestionCell
    );
    if (byQuestionCell !== -1) {
      return byQuestionCell;
    }
  }

  const rowQuestionText = String(
    row.question ?? sourceQuestion.questionText ?? ''
  ).trim();
  if (rowQuestionText) {
    const byQuestionText = questions.findIndex((question) => {
      const questionText = String(question?.questionText ?? '').trim();
      if (questionText !== rowQuestionText) {
        return false;
      }
      if (!rowSheetKey) {
        return true;
      }
      return (
        question?.questionSheet === rowSheetKey ||
        question?.answerSheet === rowSheetKey
      );
    });
    if (byQuestionText !== -1) {
      return byQuestionText;
    }
  }

  return -1;
};

const findManifestOriginalRowIndex = (
  originalRows: any[],
  row: {
    _uuid?: string;
    sheetKey?: string;
    questionCell?: string;
    answerCell?: string;
    question?: string;
    sourceQuestion?: Record<string, any>;
  }
): number => {
  const sourceQuestion = row.sourceQuestion ?? {};
  const rowUuid = row._uuid ?? sourceQuestion._uuid;
  if (rowUuid) {
    const byUuid = originalRows.findIndex((originalRow) => {
      const originalUuid =
        originalRow?._uuid ?? originalRow?.sourceQuestion?._uuid;
      return originalUuid && String(originalUuid) === String(rowUuid);
    });
    if (byUuid !== -1) {
      return byUuid;
    }
  }

  const rowSheetKey = String(
    row.sheetKey ??
      sourceQuestion.questionSheet ??
      sourceQuestion.answerSheet ??
      ''
  );
  const rowAnswerCell = String(
    row.answerCell ?? sourceQuestion.answerCell ?? ''
  );
  if (rowSheetKey && rowAnswerCell) {
    const byAnswerCell = originalRows.findIndex((originalRow) => {
      const originalSheet = String(
        originalRow?.sheetKey ??
          originalRow?.questionSheet ??
          originalRow?.answerSheet ??
          originalRow?.sourceQuestion?.questionSheet ??
          originalRow?.sourceQuestion?.answerSheet ??
          ''
      );
      const originalAnswerCell = String(
        originalRow?.answerCell ?? originalRow?.sourceQuestion?.answerCell ?? ''
      );
      return (
        originalSheet === rowSheetKey && originalAnswerCell === rowAnswerCell
      );
    });
    if (byAnswerCell !== -1) {
      return byAnswerCell;
    }
  }

  const rowQuestionCell = String(
    row.questionCell ?? sourceQuestion.questionCell ?? ''
  );
  if (rowSheetKey && rowQuestionCell) {
    const byQuestionCell = originalRows.findIndex((originalRow) => {
      const originalSheet = String(
        originalRow?.sheetKey ??
          originalRow?.questionSheet ??
          originalRow?.answerSheet ??
          originalRow?.sourceQuestion?.questionSheet ??
          originalRow?.sourceQuestion?.answerSheet ??
          ''
      );
      const originalQuestionCell = String(
        originalRow?.questionCell ??
          originalRow?.sourceQuestion?.questionCell ??
          ''
      );
      return (
        originalSheet === rowSheetKey &&
        originalQuestionCell === rowQuestionCell
      );
    });
    if (byQuestionCell !== -1) {
      return byQuestionCell;
    }
  }

  const rowQuestionText = String(
    row.question ?? sourceQuestion.questionText ?? ''
  ).trim();
  if (rowQuestionText) {
    const byQuestionText = originalRows.findIndex((originalRow) => {
      const originalQuestionText = String(
        originalRow?.question ??
          originalRow?.questionText ??
          originalRow?.sourceQuestion?.questionText ??
          ''
      ).trim();
      if (originalQuestionText !== rowQuestionText) {
        return false;
      }
      if (!rowSheetKey) {
        return true;
      }
      const originalSheet = String(
        originalRow?.sheetKey ??
          originalRow?.questionSheet ??
          originalRow?.answerSheet ??
          originalRow?.sourceQuestion?.questionSheet ??
          originalRow?.sourceQuestion?.answerSheet ??
          ''
      );
      return originalSheet === rowSheetKey;
    });
    if (byQuestionText !== -1) {
      return byQuestionText;
    }
  }

  return -1;
};

const mapManifestOriginalRowsToQuestionItems = (
  manifest: IRFP_Manifest & { original?: any[] }
): QuestionItem[] => {
  const originalRows = Array.isArray(manifest.original)
    ? manifest.original
    : [];
  const manifestQuestions = Array.isArray(manifest.questions)
    ? manifest.questions
    : [];
  const rowsToMap = originalRows.length
    ? originalRows
    : manifestQuestions.map((question) => ({
        question: question?.questionText ?? '',
        questionCell: question?.questionCell ?? '',
        answerCell: question?.answerCell ?? '',
        sheetKey: question?.questionSheet ?? question?.answerSheet ?? '',
        ignored: Boolean(
          (question as any)?.ignored ?? (question as any)?.ignore
        ),
        sourceQuestion: structuredClone(question),
        _uuid: question?._uuid
      }));

  const mappedRows = rowsToMap.map((row: any) => {
    const sourceQuestionSeed =
      row?.sourceQuestion && typeof row.sourceQuestion === 'object'
        ? row.sourceQuestion
        : {};
    const rowQuestionText =
      row?.question ??
      row?.questionText ??
      sourceQuestionSeed?.questionText ??
      '';
    const rowQuestionCell =
      row?.questionCell ?? sourceQuestionSeed?.questionCell ?? '';
    const rowAnswerCell =
      row?.answerCell ?? sourceQuestionSeed?.answerCell ?? '';
    const rowSheetValue =
      row?.sheetKey ??
      row?.questionSheet ??
      row?.answerSheet ??
      sourceQuestionSeed?.questionSheet ??
      sourceQuestionSeed?.answerSheet;
    const rowSheetKey = rowSheetValue ? String(rowSheetValue) : '';
    const rowIgnored = Boolean(
      row?.ignored ??
        row?.ignore ??
        sourceQuestionSeed?.ignored ??
        sourceQuestionSeed?.ignore
    );
    const rowUserInput = Boolean(
      row?.userInput ??
        row?.user_input ??
        sourceQuestionSeed?.userInput ??
        sourceQuestionSeed?.user_input
    );
    const rowUuid = row?._uuid ?? sourceQuestionSeed?._uuid;
    const mergedSourceQuestion = {
      ...structuredClone(sourceQuestionSeed),
      questionText: rowQuestionText || sourceQuestionSeed.questionText || '',
      questionCell: rowQuestionCell || sourceQuestionSeed.questionCell || '',
      questionSheet: rowSheetKey || sourceQuestionSeed.questionSheet || '',
      answerCell: rowAnswerCell || sourceQuestionSeed.answerCell || '',
      answerSheet: rowSheetKey || sourceQuestionSeed.answerSheet || '',
      ignored: rowIgnored,
      _uuid: rowUuid ?? sourceQuestionSeed._uuid
    };
    const manifestQuestionIndex = findManifestQuestionIndex(manifestQuestions, {
      _uuid: rowUuid,
      sheetKey: rowSheetKey,
      questionCell: rowQuestionCell,
      answerCell: rowAnswerCell,
      question: rowQuestionText,
      sourceQuestion: mergedSourceQuestion
    });
    const manifestQuestion =
      manifestQuestionIndex !== -1
        ? manifestQuestions[manifestQuestionIndex]
        : null;
    const answerText =
      typeof row?.answer === 'string' && row.answer.trim()
        ? row.answer
        : getLatestSourceAnswerText(
            mergedSourceQuestion,
            manifestQuestion ? getManifestAnswerText(manifestQuestion) : ''
          );

    return {
      question: rowQuestionText,
      answer: answerText,
      questionCell: rowQuestionCell,
      answerCell: rowAnswerCell,
      ignored: rowIgnored,
      isOriginal: originalRows.length > 0,
      sheetKey: rowSheetKey,
      userInput: rowUserInput,
      sourceQuestion: mergedSourceQuestion,
      _uuid: rowUuid
    };
  });

  const nonIgnoredRows: QuestionItem[] = [];
  const ignoredRows: QuestionItem[] = [];
  for (const row of mappedRows) {
    if (row.ignored) {
      ignoredRows.push(row);
    } else {
      nonIgnoredRows.push(row);
    }
  }

  return [...nonIgnoredRows, ...ignoredRows];
};

const QuestionnaireTable: React.FC = () => {
  const { token, auth, users, user } = useContext(UserContext);
  const {
    getDocument,
    setMainAlert,
    allDocuments,
    setAllDocuments,
    updateDocument
  } = useContext(DocumentContext);
  const [data, setData] = useState<QuestionItem[]>([]);
  const [documentRecord, setDocumentRecord] =
    useState<documentTableItem | null>(null);
  const [originalQuestionnaireJson, setOriginalQuestionnaireJson] =
    useState<QuestionnaireJson | null>(null);
  const [initialQuestionnaireJson, setInitialQuestionnaireJson] =
    useState<QuestionnaireJson | null>(null);
  const [questionnaireDataReady, setQuestionnaireDataReady] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchParams] = useSearchParams();
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [showOriginal, setShowOriginal] = useState<boolean>(false);
  const [metaData, setMetaData] = useState<any | null>(null);
  const [base64, setBase64] = useState<string | null>(null);
  const [highlightText, setHighlightText] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const [fileValue, setFileValue] = useState<File | null>(null);
  const [fileType, setFileType] = useState<string | null>(null);
  const [publishLoading, setPublishLoading] = useState<boolean>(false);
  const [saveJsonLoading, setSaveJsonLoading] = useState<boolean>(false);
  const [saveManifestLoading, setSaveManifestLoading] =
    useState<boolean>(false);
  const [saveJsonStatus, setSaveJsonStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });
  const [userInputIgnoreSnapshot, setUserInputIgnoreSnapshot] = useState<Record<
    number,
    boolean
  > | null>(null);
  const [showHelpModal, setShowHelpModal] = useState<boolean>(false);
  const [validationDetails, setValidationDetails] = useState<{
    visible: boolean;
    questionIndex: number | null;
    entry: ValidationAuditEntry | null;
  }>({
    visible: false,
    questionIndex: null,
    entry: null
  });

  // document selector state
  const [KBChecked, setKBChecked] = useState<boolean>(false);
  const [kbFilters, setKBFilters] = useState<OptionDefinition[]>([]);
  const [prevChecked, setPrevChecked] = useState<boolean>(false);
  const [selectedDocuments, setSelectedDocuments] = useState<
    OptionDefinition[]
  >([]);
  const [similarityThreshold, setSimilarThreshold] = useState<number>(0.8);
  const [showDocumentSelector, setShowDocumentSelector] =
    useState<boolean>(false);
  const [uploadProcessing, setUploadProcessing] = useState<boolean>(false);

  // state related to rfp answers

  const [manifestValue, setManifestValue] =
    useState<ManifestWithOriginal | null>(null);
  const [initialManifestSha, setInitialManifestSha] = useState<string>('');
  const [currentManifestSha, setCurrentManifestSha] = useState<string>('');
  const [loadingDocs, setLoadingDocs] = useState<boolean>(true);

  // refs and navs

  const refScroll = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const fetchDocumentFromContext = async (
    documentId: string
  ): Promise<void> => {
    try {
      const document = (await getDocument(
        documentId
      )) as documentTableItem | null;
      if (!document?._id) {
        return;
      }
      setDocumentRecord(document);
      setFileType(getViewerFileTypeFromDocument(document));
    } catch (error) {
      logger.error('Error fetching document record from context', error);
    }
  };

  useEffect(() => {
    if (!documentRecord?._id) {
      return;
    }

    if (
      documentRecord.documentType === DOCTYPE.rfp &&
      Boolean(documentRecord.processed)
    ) {
      getManifestFile(documentRecord._id);
      return;
    }

    fetchQuestionnaireData(documentRecord._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentRecord]);

  useEffect(() => {
    const id = searchParams.get('id');
    if (!id) {
      return;
    }
    fetchDocumentFromContext(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const id = searchParams.get('id');

    if (!id || !showOriginal) {
      return;
    }
    if (!base64 && fileType === 'pdf') {
      getPdf(id);
    }
    if (!fileValue && fileType === 'sheet') {
      fetchSheetAsFile(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOriginal, fileType, base64, fileValue]);

  async function getPdf(documentId: string) {
    try {
      const presignedUrl = await axios.post(
        `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=getQuestionnaireOriginalPDF&ext=pdf`,
        {
          documentId
        },
        auth
      );
      const base64Temp = await fetchPDFAsBase64(presignedUrl.data.url);
      setBase64(base64Temp);
    } catch (e) {
      const error = e as Error;
      console.error('Error fetching original:', error);
      setLoading(false);
    }
  }

  const fetchPDFAsBase64 = async (url: string): Promise<string> => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
          resolve(reader.result as string);
        };
        reader.onerror = reject;
      });
    } catch (e) {
      throw Error('Error Loading Document');
    }
  };
  const fetchSheetAsFile = async (documentId: string): Promise<void> => {
    const presignedUrl = await axios.post(
      `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=getQuestionnaireOriginalPDF&ext=xlsx`,
      {
        documentId
      },
      auth
    );
    const response = await axios.get(presignedUrl.data.url, {
      responseType: 'blob'
    });
    const file = new File([response.data], `Original - Sheet.xlsx`, {
      type: response.data.type
    });
    setFileValue(file);
  };

  // Dummy fetch function - replace with your actual implementation
  const fetchQuestionnaireData = async (documentId: string): Promise<void> => {
    if (!token) return;
    try {
      setLoading(true);
      setQuestionnaireDataReady(false);
      setManifestValue(null);
      setInitialManifestSha('');
      setCurrentManifestSha('');

      const presignedUrl = await axios.post(
        `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=getQuestionnaire`,
        {
          documentId
        },
        auth
      );
      console.log('presigned url', presignedUrl.data.url);
      const response = await axios.get(presignedUrl.data.url, {
        responseType: 'json'
      });
      console.log('response', response.data);

      const { $meta_data, ...rest } = response.data;
      console.log('rest data', rest);
      console.log('metaData', $meta_data);
      setMetaData($meta_data);
      setFileType((previousFileType) => {
        if (previousFileType) {
          return previousFileType;
        }
        if ($meta_data.documentType === 'xlsx') {
          return 'sheet';
        }
        if ($meta_data.documentType === 'pdf') {
          return 'pdf';
        }
        return null;
      });
      // setData(response.data.pages[0].pageQuestions);
      const resultData: QuestionItem[] = [];
      if ($meta_data.documentType === 'pdf') {
        const pdfPageEntries: Array<[string, any]> = [];

        if (Array.isArray(rest.pages)) {
          rest.pages.forEach((page: any, index: number) => {
            const pageKey = getPdfPageKey(page, index);
            pdfPageEntries.push([pageKey, page]);
          });
        } else if (rest.pages && typeof rest.pages === 'object') {
          for (const [pageKey, pageValue] of Object.entries(rest.pages)) {
            pdfPageEntries.push([String(pageKey), pageValue]);
          }
        } else {
          for (const [pageKey, pageValue] of Object.entries(rest)) {
            if (pageKey === 'pages') {
              continue;
            }
            if (!pageValue || typeof pageValue !== 'object') {
              continue;
            }
            pdfPageEntries.push([String(pageKey), pageValue]);
          }
        }

        for (const [pageKey, pageValue] of pdfPageEntries) {
          const pageQuestions = Array.isArray(pageValue?.pageQuestions)
            ? pageValue.pageQuestions.map((ele: any) => ({
                ...ele,
                question: ele.question ?? '',
                answer: ele.answer ?? '',
                questionCell: ele.questionCell ?? '',
                answerCell: ele.answerCell ?? '',
                sheetKey: pageKey,
                ignored: Boolean(ele.ignored),
                userInput: Boolean(ele.user_input ?? ele.userInput),
                isOriginal: false,
                sourceQuestion: structuredClone(ele)
              }))
            : [];
          resultData.push(...pageQuestions);
        }
      }
      if ($meta_data.documentType === 'xlsx') {
        const keys = Object.keys(rest);
        for (const key of keys) {
          const pageQuestions = rest[key].pages[0].pageQuestions.map(
            (item: any) => ({
              ...item,
              question: item.question ?? '',
              answer: item.answer ?? '',
              questionCell: item.questionCell ?? '',
              answerCell: item.answerCell ?? '',
              sheetKey: key,
              ignored: Boolean(item.ignored),
              userInput: Boolean(item.user_input ?? item.userInput),
              isOriginal: false,
              sourceQuestion: structuredClone(item)
            })
          );
          resultData.push(...pageQuestions);
        }
      }
      const normalizedQuestionnaireJson = syncDataToQuestionnaireJson(
        response.data as QuestionnaireJson,
        resultData
      );
      setOriginalQuestionnaireJson(
        structuredClone(normalizedQuestionnaireJson)
      );
      setInitialQuestionnaireJson(structuredClone(normalizedQuestionnaireJson));
      setData(resultData);
      setUserInputIgnoreSnapshot(null);
      setQuestionnaireDataReady(true);
      // Dummy data for demonstration
      // setTimeout(() => {
      //   setData(resultData);
      //   setLoading(false);
      // }, 500);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching questionnaire data:', error);
      setOriginalQuestionnaireJson(null);
      setInitialQuestionnaireJson(null);
      setQuestionnaireDataReady(false);
      setLoading(false);
    }
  };

  const isProcessedRfpMode = (): boolean =>
    documentRecord?.documentType === DOCTYPE.rfp &&
    Boolean(documentRecord?.processed);

  const handleIgnoreToggle = (index: number): void => {
    if (documentRecord?.finalized) {
      return;
    }

    if (isProcessedRfpMode()) {
      const row = tableData[index];
      if (!row || (row.isOriginal && row.ignored)) {
        return;
      }
      const nextIgnored = !Boolean(row.ignored);

      setManifestValue((previousManifest) => {
        if (!previousManifest) {
          return previousManifest;
        }

        const nextManifest = structuredClone(previousManifest);
        let hasChanges = false;

        if (Array.isArray(nextManifest.questions)) {
          const questionIndex = findManifestQuestionIndex(
            nextManifest.questions,
            row
          );
          if (questionIndex !== -1) {
            const manifestQuestion = nextManifest.questions[
              questionIndex
            ] as any;
            manifestQuestion.ignored = nextIgnored;
            manifestQuestion.ignore = nextIgnored;
            hasChanges = true;
          }
        }

        if (Array.isArray(nextManifest.original)) {
          const originalIndex = findManifestOriginalRowIndex(
            nextManifest.original,
            row
          );
          if (originalIndex !== -1) {
            const originalRow = nextManifest.original[originalIndex];
            originalRow.ignored = nextIgnored;
            originalRow.ignore = nextIgnored;
            const sourceQuestion =
              originalRow?.sourceQuestion &&
              typeof originalRow.sourceQuestion === 'object'
                ? originalRow.sourceQuestion
                : {};
            originalRow.sourceQuestion = {
              ...sourceQuestion,
              ignored: nextIgnored,
              ignore: nextIgnored
            };
            hasChanges = true;
          }
        }

        return hasChanges ? nextManifest : previousManifest;
      });
      return;
    }

    setData((prevData) =>
      prevData.map((item, i) => {
        if (i !== index) {
          return item;
        }

        if (item.isOriginal && item.ignored) {
          return item;
        }

        return { ...item, ignored: !item.ignored };
      })
    );
  };

  const handleIgnoreUserInput = (): void => {
    if (isProcessedRfpMode()) {
      return;
    }

    if (userInputIgnoreSnapshot) {
      const nextData = data.map((item, index) => {
        if (!item.userInput) {
          return item;
        }
        if (!(index in userInputIgnoreSnapshot)) {
          return item;
        }
        return { ...item, ignored: userInputIgnoreSnapshot[index] };
      });
      setData(nextData);
      setUserInputIgnoreSnapshot(null);
      return;
    }

    const nextSnapshot: Record<number, boolean> = {};
    const nextData = data.map((item, index) => {
      if (!item.userInput) {
        return item;
      }
      nextSnapshot[index] = Boolean(item.ignored);
      return { ...item, ignored: true };
    });

    setData(nextData);
    setUserInputIgnoreSnapshot(
      Object.keys(nextSnapshot).length ? nextSnapshot : null
    );
  };

  const handleEditStart = (
    index: number,
    field: 'question' | 'answer',
    currentValue: string
  ): void => {
    setEditingCell({ index, field });
    setEditValue(currentValue);
  };

  const handleAddRowForSheet = (sheetKey: string): void => {
    setData((prevData) => {
      const newItem: QuestionItem = {
        question: '',
        answer: '',
        questionCell: '',
        answerCell: '',
        ignored: false,
        isOriginal: false,
        sheetKey,
        userInput: false
      };

      let lastSheetRowIndex = -1;
      for (let i = prevData.length - 1; i >= 0; i--) {
        if (prevData[i].sheetKey === sheetKey) {
          lastSheetRowIndex = i;
          break;
        }
      }

      if (lastSheetRowIndex === -1) {
        return [...prevData, newItem];
      }

      const nextData = [...prevData];
      nextData.splice(lastSheetRowIndex + 1, 0, newItem);
      return nextData;
    });
  };

  const handleEditSave = (): void => {
    if (!editingCell) {
      return;
    }

    const editingRow = tableData[editingCell.index] ?? null;
    if (isProcessedRfpMode()) {
      if (!editingRow) {
        setEditingCell(null);
        setEditValue('');
        return;
      }

      if (editingCell.field === 'answer') {
        appendAnswerToManifestQuestion(editingRow, editValue);
      } else {
        updateQuestionTextInManifest(editingRow, editValue);
      }

      setEditingCell(null);
      setEditValue('');
      return;
    }

    setData((prevData) =>
      prevData.map((item, i) => {
        if (i !== editingCell.index) {
          return item;
        }

        if (editingCell.field !== 'answer') {
          return { ...item, [editingCell.field]: editValue };
        }

        const sourceQuestion = item.sourceQuestion
          ? { ...item.sourceQuestion }
          : {};
        const answerHistory = Array.isArray(sourceQuestion.answer)
          ? [...sourceQuestion.answer]
          : [];
        if (!answerHistory.length) {
          const currentAnswerText = getLatestSourceAnswerText(
            sourceQuestion,
            item.answer ?? ''
          );
          if (currentAnswerText && currentAnswerText !== editValue) {
            answerHistory.push({
              text: currentAnswerText,
              user: 'system'
            });
          }
        }
        const lastAnswerText =
          typeof answerHistory[answerHistory.length - 1]?.text === 'string'
            ? answerHistory[answerHistory.length - 1].text
            : '';

        if (lastAnswerText !== editValue) {
          answerHistory.push({
            text: editValue,
            user: user?.name ?? 'system'
          });
        }

        return {
          ...item,
          answer: editValue,
          sourceQuestion: {
            ...sourceQuestion,
            answer: answerHistory,
            answerText: editValue
          }
        };
      })
    );
    setEditingCell(null);
    setEditValue('');
  };

  const handleEditCancel = (): void => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEditSave();
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  };
  // function handleHighlight() {
  //   if (editingCell) {
  //     const textToHighlight = data[editingCell.index].question;
  //     console.log('text to highlight', textToHighlight);
  //     setHighlightText(textToHighlight);
  //   }
  // }

  useEffect(() => {
    if (refScroll.current && highlightText) {
      const count = findAndMarkInNode(refScroll.current, highlightText);
      console.log('count 888888888==========', count);
    }
  }, [highlightText]);

  useEffect(() => {
    if (!questionnaireDataReady) {
      return;
    }
    setOriginalQuestionnaireJson((previousJson) => {
      if (!previousJson) {
        return previousJson;
      }
      return syncDataToQuestionnaireJson(previousJson, data);
    });
  }, [data, questionnaireDataReady]);

  const putQuestionnaireJsonToS3 = async (
    documentId: string,
    questionnaireJson: QuestionnaireJson,
    destination: string
  ) => {
    const presignedUrl = await axios.post(
      `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=${destination}`,
      {
        documentId
      },
      auth
    );
    const saveUrl = presignedUrl?.data?.url;
    if (!saveUrl) {
      throw new Error('No presigned URL found for questionnaire save.');
    }
    if (saveUrl === 'locked') {
      throw new Error('File locked by another user.');
    }

    const jsonPayload = JSON.stringify(questionnaireJson);
    const blob = new Blob([jsonPayload], { type: 'application/json' });
    await axios.put(saveUrl, blob, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  };

  const publishPrefQandA = async (documentId: string, rows: QuestionItem[]) => {
    try {
      if (!token || metaData?.published) return;
      setPublishLoading(true);
      const sentenceArray = rows.reduce((acc, curr) => {
        if (!curr.ignored) {
          const tempValue = {
            question: curr.question,
            answer: curr.answer
          };
          acc.push(tempValue);
        }
        return acc;
      }, [] as any[]);
      const postResult = await axios.post(
        `${process.env.REACT_APP_KORU_API}/sentence?type=addPrevQA`,
        { documentId, sentenceArray },
        auth
      );
      logger.info('result', postResult);
      setMetaData((prev: any) => ({
        ...(prev ?? {}),
        published: true
      }));

      if (originalQuestionnaireJson) {
        const publishedQuestionnaireJson = structuredClone(
          originalQuestionnaireJson
        );
        if (!publishedQuestionnaireJson.$meta_data) {
          publishedQuestionnaireJson.$meta_data = {};
        }
        publishedQuestionnaireJson.$meta_data.published = true;
        setOriginalQuestionnaireJson(publishedQuestionnaireJson);

        try {
          await putQuestionnaireJsonToS3(
            documentId,
            publishedQuestionnaireJson,
            QUESTIONNAIRE_SAVE_PRESIGNED_TYPE
          );
          setInitialQuestionnaireJson(
            structuredClone(publishedQuestionnaireJson)
          );
        } catch (saveError) {
          logger.error('publish metadata save error', saveError);
          setSaveJsonStatus({
            type: 'error',
            message: 'Q&A published, but metadata save failed. Click Save JSON.'
          });
        }
      }
    } catch (e) {
      logger.error('error posting prev qanda', { e });
    } finally {
      setPublishLoading(false);
    }
  };

  const isPrevRfpDocument = documentRecord?.documentType === DOCTYPE.prevRfp;
  const isCurrentRfpDocument = documentRecord?.documentType === DOCTYPE.rfp;
  const answeringRFP =
    isCurrentRfpDocument && Boolean(documentRecord?.processed);
  const showRfpAnswerPanel = answeringRFP;
  const tableData = useMemo<QuestionItem[]>(() => {
    if (!showRfpAnswerPanel) {
      return data;
    }
    if (!manifestValue) {
      return [];
    }
    return mapManifestOriginalRowsToQuestionItems(manifestValue);
  }, [showRfpAnswerPanel, data, manifestValue]);
  useEffect(() => {
    let cancelled = false;

    const syncManifestSha = async () => {
      if (!showRfpAnswerPanel || !manifestValue) {
        if (!cancelled) {
          setCurrentManifestSha('');
        }
        return;
      }

      const nextManifestSha = await computeManifestSha(manifestValue);
      if (!cancelled) {
        setCurrentManifestSha(nextManifestSha);
      }
    };

    void syncManifestSha();
    return () => {
      cancelled = true;
    };
  }, [showRfpAnswerPanel, manifestValue]);

  const hasProcessedManifestChanges =
    showRfpAnswerPanel &&
    Boolean(initialManifestSha) &&
    Boolean(currentManifestSha) &&
    initialManifestSha !== currentManifestSha;
  const nonIgnoredQuestions = tableData.filter((item) => !item.ignored);
  const hasActiveUserInputQuestions = tableData.some(
    (item) => item.userInput && !item.ignored
  );
  const canUndoIgnoreUserInput = userInputIgnoreSnapshot !== null;
  const disableIgnoreUserInputButton =
    showRfpAnswerPanel ||
    (!canUndoIgnoreUserInput && !hasActiveUserInputQuestions);
  const canPublishQandA = nonIgnoredQuestions.every(
    (item) => item.answer.trim() !== ''
  );
  const allNonIgnoredAnswersValidated =
    nonIgnoredQuestions.length > 0 &&
    nonIgnoredQuestions.every((item) =>
      Boolean(
        normalizeValidationAuditEntries(
          item.sourceQuestion?.validatedBy,
          ''
        ).at(-1)?.valid
      )
    );
  const hasQuestionnaireJsonChanges = useMemo(() => {
    if (!originalQuestionnaireJson || !initialQuestionnaireJson) {
      return false;
    }
    return (
      JSON.stringify(originalQuestionnaireJson) !==
      JSON.stringify(initialQuestionnaireJson)
    );
  }, [originalQuestionnaireJson, initialQuestionnaireJson]);
  const isQuestionnairePublished = Boolean(
    metaData?.published || originalQuestionnaireJson?.$meta_data?.published
  );
  const publishActionDisabled =
    !canPublishQandA || publishLoading || isQuestionnairePublished;
  const finalizeActionDisabled =
    publishLoading || Boolean(documentRecord?.finalized);
  const ignoreToggleDisabledByFinalize = Boolean(documentRecord?.finalized);
  const selectedRfpPanelIndex = useMemo(() => {
    if (!showRfpAnswerPanel || tableData.length < 1) {
      return null;
    }
    if (
      currentIndex !== null &&
      currentIndex >= 0 &&
      currentIndex < tableData.length
    ) {
      return currentIndex;
    }
    return 0;
  }, [showRfpAnswerPanel, tableData, currentIndex]);
  const selectedRfpPanelRow =
    selectedRfpPanelIndex !== null ? tableData[selectedRfpPanelIndex] : null;
  const selectedRfpPanelCell = useMemo<ISelectedCell>(
    () => ({
      sheet: selectedRfpPanelRow?.sheetKey ?? '',
      cell: selectedRfpPanelRow?.answerCell ?? ''
    }),
    [selectedRfpPanelRow]
  );
  const selectedRfpPanelQuestion = useMemo<IRFP_Questions | null>(() => {
    if (!selectedRfpPanelRow) {
      return null;
    }

    const sourceQuestion = selectedRfpPanelRow.sourceQuestion ?? {};
    const manifestQuestionIndex = Array.isArray(manifestValue?.questions)
      ? findManifestQuestionIndex(
          manifestValue?.questions!,
          selectedRfpPanelRow
        )
      : -1;
    const manifestQuestion =
      manifestQuestionIndex !== -1 && Array.isArray(manifestValue?.questions)
        ? manifestValue?.questions[manifestQuestionIndex]
        : null;
    const validationHistory = normalizeValidationAuditEntries(
      sourceQuestion.validatedBy ?? manifestQuestion?.validatedBy,
      ''
    );
    const latestValidation = validationHistory.at(-1);
    const latestValidEntry = [...validationHistory]
      .reverse()
      .find((entry) => entry.valid);
    const sourceAnswerArray = Array.isArray(sourceQuestion.answer)
      ? sourceQuestion.answer
      : [];
    const manifestAnswerArray = Array.isArray(manifestQuestion?.answer)
      ? manifestQuestion?.answer
      : [];
    const baseAnswerArray = sourceAnswerArray.length
      ? sourceAnswerArray
      : manifestAnswerArray;
    const normalizedAnswers = baseAnswerArray?.length
      ? baseAnswerArray.map((answer: any) => ({
          text: typeof answer?.text === 'string' ? answer.text : '',
          user: typeof answer?.user === 'string' ? answer.user : 'system',
          ...(typeof answer?.textInternal === 'string'
            ? { textInternal: answer.textInternal }
            : typeof answer?.internalText === 'string'
              ? { textInternal: answer.internalText }
              : {})
        }))
      : [
          {
            text: getLatestSourceAnswerText(
              sourceQuestion,
              selectedRfpPanelRow.answer
            ),
            user: 'system',
            ...(() => {
              const manifestInternalText =
                typeof manifestQuestion?.answer?.[0]?.textInternal === 'string'
                  ? manifestQuestion?.answer?.[0]?.textInternal
                  : '';
              const fallbackInternalText =
                (typeof sourceQuestion?.textInternal === 'string'
                  ? sourceQuestion.textInternal
                  : '') ||
                (typeof sourceQuestion?.internalText === 'string'
                  ? sourceQuestion.internalText
                  : '') ||
                manifestInternalText;
              return fallbackInternalText
                ? { textInternal: fallbackInternalText }
                : {};
            })()
          }
        ];

    const normalizedValidation =
      sourceQuestion.validation &&
      (Array.isArray(sourceQuestion.validation) ||
        typeof sourceQuestion.validation === 'object')
        ? sourceQuestion.validation
        : manifestQuestion?.validation &&
            (Array.isArray(manifestQuestion.validation) ||
              typeof manifestQuestion.validation === 'object')
          ? manifestQuestion.validation
          : ({ text: 'Pass' } as IRFP_Questions['validation']);
    const sourceAnswerValidationList = sourceQuestion.answerValidationList;
    const manifestAnswerValidationList = manifestQuestion?.answerValidationList;
    const normalizedAnswerValidationList: string[] = Array.isArray(
      sourceAnswerValidationList
    )
      ? sourceAnswerValidationList.filter(
          (entry: unknown): entry is string => typeof entry === 'string'
        )
      : Array.isArray(manifestAnswerValidationList)
        ? manifestAnswerValidationList.filter(
            (entry: unknown): entry is string => typeof entry === 'string'
          )
        : [];
    const sourceEmbedding = sourceQuestion.embedding;
    const manifestEmbedding = manifestQuestion?.embedding;
    const normalizedEmbedding = Array.isArray(sourceEmbedding)
      ? sourceEmbedding
      : Array.isArray(manifestEmbedding)
        ? manifestEmbedding
        : [];
    const sourcePreviousAnswer = sourceQuestion.previousAnswer;
    const manifestPreviousAnswer = manifestQuestion?.previousAnswer;
    const normalizedPreviousAnswer = Array.isArray(sourcePreviousAnswer)
      ? sourcePreviousAnswer
      : Array.isArray(manifestPreviousAnswer)
        ? manifestPreviousAnswer
        : [];
    const sourceContext = sourceQuestion.context;
    const manifestContext = manifestQuestion?.context;
    const normalizedContext = Array.isArray(sourceContext)
      ? sourceContext
      : Array.isArray(manifestContext)
        ? manifestContext
        : [];

    return {
      questionText:
        sourceQuestion.questionText ?? selectedRfpPanelRow.question ?? '',
      questionCell:
        sourceQuestion.questionCell ?? selectedRfpPanelRow.questionCell ?? '',
      questionSheet:
        sourceQuestion.questionSheet ?? selectedRfpPanelRow.sheetKey ?? '',
      answerCell:
        sourceQuestion.answerCell ?? selectedRfpPanelRow.answerCell ?? '',
      answer: normalizedAnswers.length
        ? normalizedAnswers
        : [{ text: '', user: 'system' }],
      answerSheet:
        sourceQuestion.answerSheet ?? selectedRfpPanelRow.sheetKey ?? '',
      answerValidationList: normalizedAnswerValidationList,
      userValidated: Boolean(
        latestValidation?.valid ?? sourceQuestion.userValidated
      ),
      validatedBy: latestValidEntry ? [latestValidEntry.name] : [],
      embedding: normalizedEmbedding,
      previousAnswer: normalizedPreviousAnswer,
      context: normalizedContext,
      validation: normalizedValidation,
      answerText: getLatestSourceAnswerText(
        sourceQuestion,
        selectedRfpPanelRow.answer
      ),
      _uuid: sourceQuestion._uuid ?? selectedRfpPanelRow._uuid
    };
  }, [selectedRfpPanelRow, manifestValue]);

  const handlePublishClick = async () => {
    const documentId = searchParams.get('id');
    if (
      !documentId ||
      !canPublishQandA ||
      publishLoading ||
      isQuestionnairePublished
    ) {
      return;
    }
    await publishPrefQandA(documentId, tableData);
  };

  const uploadManifestToS3 = async (documentId: string): Promise<void> => {
    if (!manifestValue) {
      throw new Error('No manifest available to save.');
    }
    const presignedUrl = await axios.post(
      `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=${QUESTIONNARE_RESAVE}`,
      {
        documentId
      },
      auth
    );
    const uploadUrl = presignedUrl?.data?.url;
    if (!uploadUrl || uploadUrl === 'locked') {
      throw new Error('No upload URL for manifest.');
    }
    console.log('manifestVale', manifestValue.questions);
    const jsonPayload = JSON.stringify(manifestValue);

    const blob = new Blob([jsonPayload], { type: 'application/json' });
    await axios.put(uploadUrl, blob, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  };

  const handleSaveManifestClick = async (): Promise<void> => {
    if (
      !documentRecord?._id ||
      documentRecord?.finalized ||
      saveManifestLoading ||
      !showRfpAnswerPanel
    ) {
      return;
    }

    try {
      setSaveManifestLoading(true);
      setSaveJsonStatus({ type: null, message: '' });
      await uploadManifestToS3(documentRecord._id);
      const savedManifestSha = await computeManifestSha(manifestValue);
      setInitialManifestSha(savedManifestSha);
      setCurrentManifestSha(savedManifestSha);
      setSaveJsonStatus({
        type: 'success',
        message: 'Manifest saved.'
      });
    } catch (error) {
      logger.error('error saving manifest', error);
      setSaveJsonStatus({
        type: 'error',
        message: 'Error saving manifest.'
      });
    } finally {
      setSaveManifestLoading(false);
    }
  };

  const Finalize = async (): Promise<void> => {
    if (!documentRecord?._id || finalizeActionDisabled) {
      return;
    }

    try {
      setPublishLoading(true);
      await uploadManifestToS3(documentRecord._id);
      const savedManifestSha = await computeManifestSha(manifestValue);
      setInitialManifestSha(savedManifestSha);
      setCurrentManifestSha(savedManifestSha);

      const result = await updateDocument(
        { finalized: true, status: 'Published' },
        documentRecord._id
      );
      const updatedDoc =
        result && typeof result === 'object'
          ? ({ ...documentRecord, ...result } as documentTableItem)
          : ({ ...documentRecord, finalized: true } as documentTableItem);

      setDocumentRecord(updatedDoc);
      setAllDocuments((prevDocuments) =>
        prevDocuments.map((doc) =>
          doc._id === documentRecord._id ? { ...doc, ...updatedDoc } : doc
        )
      );
    } catch (error) {
      setMainAlert({
        alertType: 'error',
        message: 'Error finalizing document.',
        showAlert: true
      });
    } finally {
      setPublishLoading(false);
    }
  };
  // saves manifest file to s3.
  const saveQuestionnaireJsonToS3 = async (documentId: string) => {
    if (!manifestValue) {
      return;
    }
    try {
      setSaveJsonLoading(true);
      setSaveJsonStatus({ type: null, message: '' });
      console.log('Manifest before save.', manifestValue.questions);
      await putQuestionnaireJsonToS3(
        documentId,
        manifestValue,
        QUESTIONNARE_RESAVE
      );
      setInitialQuestionnaireJson(structuredClone(manifestValue));

      setSaveJsonStatus({
        type: 'success',
        message: 'Questionnaire JSON saved.'
      });
    } catch (error) {
      const err = error as Error;
      logger.error('error saving questionnaire json', err.message);
      setSaveJsonStatus({
        type: 'error',
        message: 'Error saving questionnaire JSON.'
      });
    } finally {
      setSaveJsonLoading(false);
    }
  };

  const handleSaveJsonClick = async () => {
    const documentId = searchParams.get('id');
    if (!documentId || saveJsonLoading || !manifestValue) {
      return;
    }
    // only to save an existing manifest file that has been processed.
    await saveQuestionnaireJsonToS3(documentId);
  };
  const handleCancel = () => {
    setKBChecked(false);
    setKBFilters([]);
    setPrevChecked(false);
    setSelectedDocuments([]);
    setShowDocumentSelector(false);
  };
  const handleUploadQuestionsForGeneration = async () => {
    if (uploadProcessing) {
      return;
    }
    setUploadProcessing(true);
    const questionsArray = tableData.reduce((acc, curr) => {
      if (!curr.ignored) {
        const temp = {
          questionText: curr.question,
          questionCell: null,
          questionSheet: curr.sheetKey,
          answerCell: null,
          answer: [],
          answerSheet: curr.sheetKey,
          answerText: '',
          answerValidation: null,
          answerValidationList: [] as any[],
          userValidated: false,
          validatedBy: [],
          _uuid: curr?._uuid ?? null
        };
        acc.push(temp);
      }
      return acc;
    }, [] as any[]);
    console.log('Questions array', questionsArray);
    try {
      const searchDocs = {
        KBChecked,
        kbFilters: kbFilters.map((ele) => ele.value),
        prevChecked,
        selectedDocuments: selectedDocuments.map((ele) => ele.value),
        thresholdScore: similarityThreshold
      };
      console.log('Search Docs', searchDocs);
      let enoughCredit = false;
      const creditHold = await axios.post(
        `${process.env.REACT_APP_KORU_API}/credit?type=createCreditHold`,
        {
          type: 'rfp',
          documentId: documentRecord?._id,
          kbChecked: searchDocs.KBChecked,
          prevChecked,
          numOfQuestions: questionsArray.length,
          documentIds: searchDocs.selectedDocuments
        },
        auth
      );
      console.log('credit hold === ', creditHold);
      if (
        creditHold?.data?.result?._id ||
        creditHold?.data?.result?.message === 'enough credit'
      ) {
        enoughCredit = true;
      }
      if (enoughCredit) {
        console.log('uploading file');
        const presignedUrl = await axios.post(
          `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=uploadRFPManifest`,
          {
            documentId: documentRecord?._id
          },
          auth
        );
        // upload file

        const jsonPayload = JSON.stringify({
          questions: questionsArray,
          original: tableData,
          searchDocs
        });
        const blob = new Blob([jsonPayload], { type: 'application/json' });
        const putResult = await axios.put(presignedUrl.data.url, blob, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        const allDocsTemp = allDocuments;
        const updatedDocs = allDocsTemp.reduce((acc, curr) => {
          if (curr._id === documentRecord?._id) {
            curr.status = 'building answers';
          }
          acc.push(curr);
          return acc;
        }, [] as documentTableItem[]);
        setAllDocuments(updatedDocs);
        setUploadProcessing(false);
        navigate(
          `/app/projects/${documentRecord?.project}/folder/${documentRecord?.folder}`
        );
      } else {
        setUploadProcessing(false);
        // setShowCoinsModal({ visible: true, type: 'BuyMore' });
      }
    } catch (e) {
      const error = e as Error;
      setMainAlert({
        message: `${error.message}`,
        showAlert: true,
        alertType: 'error'
      });
      setUploadProcessing(false);
      throw Error('Upload failed');
    }
  };

  const deepClone = <T,>(x: T): T => structuredClone(x);
  const getManifestFile = async (docId: string) => {
    if (!docId) return;
    logger.info('getting manifest file');
    setLoading(true);
    setQuestionnaireDataReady(false);
    try {
      const presignedUrl = await axios.post(
        `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=getDocumentRFPManifest`,
        {
          documentId: `${docId}`
        },
        auth
      );

      const response = await axios.get(presignedUrl.data.url);
      const jsonFile = response.data as ManifestWithOriginal;
      console.log('jsonFile', jsonFile?.questions);
      if (jsonFile) {
        const clonedManifest = deepClone(jsonFile);
        const initialSha = await computeManifestSha(clonedManifest);
        setManifestValue(clonedManifest);
        setInitialManifestSha(initialSha);
        setCurrentManifestSha(initialSha);
        setUserInputIgnoreSnapshot(null);
        setOriginalQuestionnaireJson(null);
        setInitialQuestionnaireJson(null);
        setMetaData((prevMetaData: any) => ({
          ...(prevMetaData ?? {}),
          documentFilename:
            prevMetaData?.documentFilename ??
            documentRecord?.documentFileName ??
            ''
        }));
        setLoadingDocs(false);
        setLoading(false);
      } else {
        setManifestValue(null);
        setInitialManifestSha('');
        setCurrentManifestSha('');
        setLoadingDocs(false);
        setLoading(false);
      }
    } catch (e) {
      setManifestValue(null);
      setInitialManifestSha('');
      setCurrentManifestSha('');
      setMainAlert({
        alertType: 'error',
        message: 'Error getting manifest file.',
        showAlert: true
      });
      setLoadingDocs(false);
      setLoading(false);
    }
  };
  const getValidationHistory = (item: QuestionItem): ValidationAuditEntry[] =>
    normalizeValidationAuditEntries(item.sourceQuestion?.validatedBy, '');

  const appendAnswerToManifestQuestion = (
    row: QuestionItem | null | undefined,
    answerText: string
  ): void => {
    if (!row) {
      return;
    }

    setManifestValue((previousManifest) => {
      if (!previousManifest) {
        return previousManifest;
      }

      const nextManifest = structuredClone(previousManifest);
      let hasChanges = false;

      const pushToAnswerHistory = (target: Record<string, any>) => {
        const answerHistory = Array.isArray(target.answer)
          ? [...target.answer]
          : [];
        if (!answerHistory.length) {
          const fallbackAnswerText =
            (typeof target.answerText === 'string' &&
              target.answerText.trim()) ||
            (typeof row.answer === 'string' && row.answer.trim()) ||
            '';
          if (fallbackAnswerText && fallbackAnswerText !== answerText) {
            answerHistory.push({
              text: fallbackAnswerText,
              user: 'system'
            });
          }
        }
        const lastAnswerText =
          typeof answerHistory[answerHistory.length - 1]?.text === 'string'
            ? answerHistory[answerHistory.length - 1].text
            : '';

        if (lastAnswerText !== answerText) {
          answerHistory.push({
            text: answerText,
            user: user?.name ?? 'system'
          });
        }

        target.answer = answerHistory;
        target.answerText = answerText;
      };

      if (Array.isArray(nextManifest.questions)) {
        const questionIndex = findManifestQuestionIndex(
          nextManifest.questions,
          row
        );
        if (questionIndex !== -1) {
          const manifestQuestion = nextManifest.questions[
            questionIndex
          ] as Record<string, any>;
          pushToAnswerHistory(manifestQuestion);
          hasChanges = true;
        }
      }

      if (Array.isArray(nextManifest.original)) {
        const originalIndex = findManifestOriginalRowIndex(
          nextManifest.original,
          row
        );
        if (originalIndex !== -1) {
          const originalRow = nextManifest.original[originalIndex];
          const sourceQuestion =
            originalRow?.sourceQuestion &&
            typeof originalRow.sourceQuestion === 'object'
              ? { ...originalRow.sourceQuestion }
              : {};

          pushToAnswerHistory(sourceQuestion);
          originalRow.sourceQuestion = sourceQuestion;
          originalRow.answer = answerText;
          hasChanges = true;
        }
      }

      return hasChanges ? nextManifest : previousManifest;
    });
  };

  const updateQuestionTextInManifest = (
    row: QuestionItem | null | undefined,
    questionText: string
  ): void => {
    if (!row) {
      return;
    }

    setManifestValue((previousManifest) => {
      if (!previousManifest) {
        return previousManifest;
      }

      const nextManifest = structuredClone(previousManifest);
      let hasChanges = false;

      if (Array.isArray(nextManifest.questions)) {
        const questionIndex = findManifestQuestionIndex(
          nextManifest.questions,
          row
        );
        if (questionIndex !== -1) {
          nextManifest.questions[questionIndex].questionText = questionText;
          hasChanges = true;
        }
      }

      if (Array.isArray(nextManifest.original)) {
        const originalIndex = findManifestOriginalRowIndex(
          nextManifest.original,
          row
        );
        if (originalIndex !== -1) {
          const originalRow = nextManifest.original[originalIndex];
          originalRow.question = questionText;
          originalRow.questionText = questionText;
          const sourceQuestion =
            originalRow?.sourceQuestion &&
            typeof originalRow.sourceQuestion === 'object'
              ? originalRow.sourceQuestion
              : {};
          originalRow.sourceQuestion = {
            ...sourceQuestion,
            questionText
          };
          hasChanges = true;
        }
      }

      return hasChanges ? nextManifest : previousManifest;
    });
  };

  const handleValidationToggle = (index: number, valid: boolean): void => {
    const validatorName = user?.name?.trim();
    if (!validatorName) {
      return;
    }
    const now = new Date().toISOString();

    if (isProcessedRfpMode()) {
      const row = tableData[index];
      if (!row) {
        return;
      }

      setManifestValue((previousManifest) => {
        if (!previousManifest) {
          return previousManifest;
        }

        const nextManifest = structuredClone(previousManifest);
        let hasChanges = false;
        const nextAuditEntry = {
          name: validatorName,
          date: now,
          valid
        };

        if (Array.isArray(nextManifest.questions)) {
          const questionIndex = findManifestQuestionIndex(
            nextManifest.questions,
            row
          );
          if (questionIndex !== -1) {
            const question = nextManifest.questions[questionIndex] as Record<
              string,
              any
            >;
            const existingValidatedBy = normalizeValidationAuditEntries(
              question.validatedBy,
              now
            );
            question.validatedBy = [...existingValidatedBy, nextAuditEntry];
            question.userValidated = valid;
            hasChanges = true;
          }
        }

        if (Array.isArray(nextManifest.original)) {
          const originalIndex = findManifestOriginalRowIndex(
            nextManifest.original,
            row
          );
          if (originalIndex !== -1) {
            const originalRow = nextManifest.original[originalIndex];
            const sourceQuestion =
              originalRow?.sourceQuestion &&
              typeof originalRow.sourceQuestion === 'object'
                ? { ...originalRow.sourceQuestion }
                : {};
            const existingValidatedBy = normalizeValidationAuditEntries(
              sourceQuestion.validatedBy,
              now
            );
            sourceQuestion.validatedBy = [
              ...existingValidatedBy,
              nextAuditEntry
            ];
            sourceQuestion.userValidated = valid;
            originalRow.sourceQuestion = sourceQuestion;
            hasChanges = true;
          }
        }

        return hasChanges ? nextManifest : previousManifest;
      });
      return;
    }

    setData((prevData) =>
      prevData.map((item, itemIndex) => {
        if (itemIndex !== index) {
          return item;
        }

        const sourceQuestion = item.sourceQuestion
          ? { ...item.sourceQuestion }
          : {};
        const existingValidatedBy = normalizeValidationAuditEntries(
          sourceQuestion.validatedBy,
          now
        );
        const validatedBy = [
          ...existingValidatedBy,
          {
            name: validatorName,
            date: now,
            valid
          }
        ];

        return {
          ...item,
          sourceQuestion: {
            ...sourceQuestion,
            userValidated: valid,
            validatedBy
          }
        };
      })
    );
  };

  const handleValidate = (index: number): void => {
    handleValidationToggle(index, true);
  };

  const handlePanelValidateAnswer = (): void => {
    if (selectedRfpPanelIndex === null) {
      return;
    }
    handleValidate(selectedRfpPanelIndex);
  };

  const handlePanelAcceptAnswer = (previousAnswerIndex?: number): void => {
    if (selectedRfpPanelIndex === null) {
      return;
    }
    const selectedRow = tableData[selectedRfpPanelIndex];
    if (!selectedRow) {
      return;
    }

    const manifestQuestionsFromValue = manifestValue?.questions;
    const manifestQuestions = Array.isArray(manifestQuestionsFromValue)
      ? manifestQuestionsFromValue
      : [];
    const manifestQuestionIndex = manifestQuestions.length
      ? findManifestQuestionIndex(manifestQuestions, selectedRow)
      : -1;
    const manifestQuestion =
      manifestQuestionIndex !== -1
        ? manifestQuestions[manifestQuestionIndex]
        : null;

    const manifestPreviousAnswers: IRFP_PreviousAnswer[] = Array.isArray(
      manifestQuestion?.previousAnswer
    )
      ? manifestQuestion?.previousAnswer ?? []
      : [];
    const hasValidPreviousAnswerIndex =
      typeof previousAnswerIndex === 'number' &&
      Number.isInteger(previousAnswerIndex) &&
      previousAnswerIndex >= 0 &&
      previousAnswerIndex < manifestPreviousAnswers.length;

    // No selected previous answer means "accept current answer as validated".
    if (!hasValidPreviousAnswerIndex) {
      handlePanelValidateAnswer();
      return;
    }

    const previousAnswerCandidate = hasValidPreviousAnswerIndex
      ? manifestPreviousAnswers[previousAnswerIndex]
      : null;

    const nextAnswerText =
      typeof previousAnswerCandidate?.answer === 'string'
        ? previousAnswerCandidate?.answer
        : typeof previousAnswerCandidate?.text === 'string'
          ? previousAnswerCandidate.text
          : '';

    if (!nextAnswerText.trim()) {
      handlePanelValidateAnswer();
      return;
    }

    if (isProcessedRfpMode()) {
      appendAnswerToManifestQuestion(selectedRow, nextAnswerText);
      handlePanelValidateAnswer();
      return;
    }

    setData((prevData) =>
      prevData.map((item, itemIndex) => {
        if (itemIndex !== selectedRfpPanelIndex) {
          return item;
        }

        const sourceQuestion = item.sourceQuestion
          ? { ...item.sourceQuestion }
          : {};
        const answerHistory = Array.isArray(sourceQuestion.answer)
          ? [...sourceQuestion.answer]
          : [];
        if (
          !answerHistory.length ||
          answerHistory[answerHistory.length - 1]?.text !== nextAnswerText
        ) {
          answerHistory.push({
            text: nextAnswerText,
            user: user?.name ?? 'system'
          });
        }

        return {
          ...item,
          answer: nextAnswerText,
          sourceQuestion: {
            ...sourceQuestion,
            answer: answerHistory,
            answerText: nextAnswerText
          }
        };
      })
    );
    appendAnswerToManifestQuestion(selectedRow, nextAnswerText);

    handlePanelValidateAnswer();
  };

  const handlePanelUpdateFromEdit = (
    text: string,
    _cell: ISelectedCell
  ): void => {
    if (selectedRfpPanelIndex === null) {
      return;
    }
    const selectedRow = tableData[selectedRfpPanelIndex];
    if (!selectedRow) {
      return;
    }

    if (isProcessedRfpMode()) {
      appendAnswerToManifestQuestion(selectedRow, text);
      return;
    }

    setData((prevData) =>
      prevData.map((item, itemIndex) => {
        if (itemIndex !== selectedRfpPanelIndex) {
          return item;
        }

        const sourceQuestion = item.sourceQuestion
          ? { ...item.sourceQuestion }
          : {};
        const answerHistory = Array.isArray(sourceQuestion.answer)
          ? [...sourceQuestion.answer]
          : [];
        if (!answerHistory.length) {
          const currentAnswerText = getLatestSourceAnswerText(
            sourceQuestion,
            item.answer ?? ''
          );
          if (currentAnswerText && currentAnswerText !== text) {
            answerHistory.push({
              text: currentAnswerText,
              user: 'system'
            });
          }
        }
        const lastAnswerText =
          typeof answerHistory[answerHistory.length - 1]?.text === 'string'
            ? answerHistory[answerHistory.length - 1].text
            : '';

        if (lastAnswerText !== text) {
          answerHistory.push({
            text,
            user: user?.name ?? 'system'
          });
        }

        return {
          ...item,
          answer: text,
          sourceQuestion: {
            ...sourceQuestion,
            answer: answerHistory,
            answerText: text
          }
        };
      })
    );
    appendAnswerToManifestQuestion(selectedRow, text);
  };

  const handlePanelHighlightNonValidatedCells = (): void => {
    return;
  };

  const openValidationDetails = (index: number): void => {
    const item = tableData[index];
    if (!item) {
      return;
    }

    const validationHistory = getValidationHistory(item);
    const latestValidation = validationHistory.at(-1);
    if (!latestValidation?.valid) {
      return;
    }

    setValidationDetails({
      visible: true,
      questionIndex: index,
      entry: latestValidation
    });
  };

  const closeValidationDetails = (): void => {
    setValidationDetails({
      visible: false,
      questionIndex: null,
      entry: null
    });
  };

  const handleUnvalidate = (): void => {
    if (validationDetails.questionIndex === null) {
      return;
    }
    handleValidationToggle(validationDetails.questionIndex, false);
    closeValidationDetails();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="flex flex-col items-center gap-3">
          <div
            className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin"
            style={{
              borderColor: QUESTION_TABLE_ACCENT_COLOR,
              borderTopColor: 'transparent'
            }}
          ></div>
          <p className="text-slate-600 font-medium">Loading questionnaire...</p>
        </div>
      </div>
    );
  }
  const headerTextClass = showOriginal ? 'text-xs' : 'text-sm';
  const bodyTextClass = showOriginal ? 'text-xs' : 'text-sm';
  const showOriginalPane =
    showOriginal &&
    ((fileType === 'pdf' && Boolean(base64)) ||
      (fileType === 'sheet' && Boolean(fileValue)));
  const sourcePreviewPaneClass = showRfpAnswerPanel
    ? 'w-[35%] min-w-[35%]'
    : 'w-[50%] min-w-[50%]';
  const tablePaneClass = showOriginalPane
    ? showRfpAnswerPanel
      ? 'w-[35%]'
      : 'w-[50%]'
    : showRfpAnswerPanel
      ? 'w-[70%]'
      : 'w-[100%]';

  return (
    <div
      className="h-[90dvh] max-h-[90dvh] min-h-0 flex gap-4 overflow-hidden rounded-2xl border border-slate-200/80 bg-white/70 p-3 shadow-[0_24px_60px_-30px_rgba(15,23,42,0.45)] backdrop-blur-sm"
      style={questionTableAccentVars}
    >
      {showOriginal && base64 && fileType === 'pdf' ? (
        <div
          ref={refScroll}
          className={clsx(
            sourcePreviewPaneClass,
            'h-full min-h-0 overflow-hidden rounded-xl border border-slate-200/80 bg-gradient-to-br from-slate-50 to-white pt-8 shadow-sm'
          )}
        >
          <PdfScrollViewer
            base64={base64}
            origin="qTable"
            // startPage={ 1}
            scale={0.8}
            scrollToPage={
              currentIndex !== null && tableData[currentIndex]
                ? Number(tableData[currentIndex].sheetKey)
                : 1
            }
          />
        </div>
      ) : null}
      {showOriginal && fileValue && fileType === 'sheet' ? (
        <div
          ref={refScroll}
          className={clsx(
            sourcePreviewPaneClass,
            'h-full min-h-0 overflow-hidden rounded-xl border border-slate-200/80 bg-gradient-to-br from-slate-50 to-white pb-8 pt-8 shadow-sm'
          )}
        >
          <QuestionsSpreadsheetViewer file={fileValue} />
        </div>
      ) : null}
      <div
        className={clsx(
          tablePaneClass,
          'h-full min-h-0 rounded-xl border border-slate-200/80 bg-gradient-to-br from-slate-50 to-white p-6 shadow-sm'
        )}
      >
        <div
          className="max-w-7xl mx-auto flex h-full min-h-0 flex-col"
          id="rightSide"
        >
          {/* Header */}
          <div className="mb-8">
            <div className="flex justify-between">
              <h1 className="text-3xl font-bold text-slate-900 mb-2">
                {metaData?.documentFilename ?? documentRecord?.documentFileName}
              </h1>
              <div
                className={clsx(
                  'flex gap-2',
                  showOriginal ? 'flex-col items-end' : 'items-center'
                )}
              >
                <button
                  onClick={() => setShowHelpModal(true)}
                  className="h-8 w-8 rounded-md border border-slate-300 bg-white text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900 flex items-center justify-center"
                  title="Help"
                  aria-label="Open help modal"
                >
                  <Info className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowOriginal(!showOriginal)}
                  className="px-3 py-1 bg-[var(--qt-accent-color)] text-white rounded-md hover:brightness-95 transition-all text-sm font-medium flex items-center gap-1"
                >
                  {!showOriginal ? 'Show Original' : 'Hide Original'}
                </button>
                {!showRfpAnswerPanel ? (
                  <button
                    onClick={handleIgnoreUserInput}
                    disabled={disableIgnoreUserInputButton}
                    className={clsx(
                      'px-3 py-1 rounded-md transition-all text-sm font-medium flex items-center gap-1',
                      disableIgnoreUserInputButton
                        ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                        : canUndoIgnoreUserInput
                          ? 'bg-slate-700 text-white hover:bg-slate-800'
                          : 'bg-amber-500 text-white hover:bg-amber-600'
                    )}
                  >
                    {canUndoIgnoreUserInput
                      ? 'Undo Ignore User Input'
                      : 'Ignore User Input'}
                  </button>
                ) : null}
                {!showRfpAnswerPanel &&
                (hasQuestionnaireJsonChanges || saveJsonLoading) ? (
                  <button
                    onClick={handleSaveJsonClick}
                    disabled={saveJsonLoading || !originalQuestionnaireJson}
                    className={clsx(
                      'px-3 py-1 rounded-md transition-all text-sm font-medium flex items-center gap-2',
                      saveJsonLoading || !originalQuestionnaireJson
                        ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                        : 'bg-[var(--qt-accent-color)] text-white hover:brightness-95'
                    )}
                  >
                    {saveJsonLoading ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        Saving JSON...
                      </>
                    ) : (
                      'Save'
                    )}
                  </button>
                ) : null}
                {showRfpAnswerPanel && !documentRecord?.finalized ? (
                  <button
                    onClick={handleSaveManifestClick}
                    disabled={saveManifestLoading || !manifestValue}
                    className={clsx(
                      'px-3 py-1 rounded-md transition-all text-sm font-medium flex items-center gap-2',
                      saveManifestLoading || !manifestValue
                        ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                        : 'bg-[var(--qt-accent-color)] text-white hover:brightness-95'
                    )}
                  >
                    {saveManifestLoading ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        Saving...
                      </>
                    ) : (
                      'Save'
                    )}
                  </button>
                ) : null}
                {isPrevRfpDocument ? (
                  <button
                    onClick={handlePublishClick}
                    disabled={publishActionDisabled}
                    className={clsx(
                      'px-3 py-1 rounded-md transition-all text-sm font-medium flex items-center gap-2',
                      publishActionDisabled
                        ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                        : 'bg-[var(--qt-accent-color)] text-white hover:brightness-95'
                    )}
                  >
                    {publishLoading ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        Publishing...
                      </>
                    ) : isQuestionnairePublished ? (
                      'Published'
                    ) : (
                      'Publish Q & A'
                    )}
                  </button>
                ) : null}
                {isCurrentRfpDocument && !showRfpAnswerPanel ? (
                  <button
                    onClick={() => setShowDocumentSelector(true)}
                    disabled={uploadProcessing}
                    className={clsx(
                      'px-3 py-1 rounded-md transition-all text-sm font-medium flex items-center gap-2',
                      uploadProcessing
                        ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                        : 'bg-[var(--qt-accent-color)] text-white hover:brightness-95'
                    )}
                  >
                    {uploadProcessing ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        Generating...
                      </>
                    ) : (
                      'Generate Answers'
                    )}
                  </button>
                ) : null}
                {showRfpAnswerPanel && allNonIgnoredAnswersValidated ? (
                  <button
                    onClick={Finalize}
                    disabled={finalizeActionDisabled}
                    className={clsx(
                      'px-3 py-1 rounded-md transition-all text-sm font-medium flex items-center gap-2',
                      finalizeActionDisabled
                        ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                        : 'bg-[var(--qt-accent-color)] text-white hover:brightness-95'
                    )}
                  >
                    {publishLoading ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        Finalizing...
                      </>
                    ) : (
                      'Finalize'
                    )}
                  </button>
                ) : null}
              </div>
            </div>
            <p className="text-slate-600">
              Review and edit responses for ID:{' '}
              <span className="font-mono font-semibold text-[var(--qt-accent-color)]">
                {searchParams.get('id') || 'N/A'}
              </span>
            </p>
            {saveJsonStatus.message ? (
              <p
                className={clsx(
                  'mt-1 text-sm',
                  saveJsonStatus.type === 'success'
                    ? 'text-green-700'
                    : 'text-red-600'
                )}
              >
                {saveJsonStatus.message}
              </p>
            ) : null}
            {showRfpAnswerPanel && hasProcessedManifestChanges ? (
              <p className="mt-1 text-sm text-amber-700">
                Manifest has unsaved changes.
              </p>
            ) : null}
          </div>

          {/* Table Container */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-slate-200 flex-1 min-h-0">
            <div className="h-full overflow-x-auto overflow-y-auto">
              <table className="w-full table-fixed">
                <thead>
                  <tr className="bg-gradient-to-r from-slate-800 to-slate-700">
                    <th
                      className={clsx(
                        'sticky top-0 z-10 px-6 py-4 text-left font-semibold text-white uppercase tracking-wider w-24 bg-gradient-to-r from-slate-800 to-slate-700',
                        headerTextClass
                      )}
                    >
                      Ignore
                    </th>
                    <th
                      className={clsx(
                        'sticky top-0 z-10 px-6 py-4 text-left font-semibold text-white uppercase tracking-wider w-1/2 bg-gradient-to-r from-slate-800 to-slate-700',
                        headerTextClass
                      )}
                    >
                      Question
                    </th>
                    <th
                      className={clsx(
                        'sticky top-0 z-10 px-6 py-4 text-left font-semibold text-white uppercase tracking-wider w-1/2 bg-gradient-to-r from-slate-800 to-slate-700',
                        headerTextClass
                      )}
                    >
                      Answer
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {tableData.map((item, index) => {
                    // Check if this is the first item or if the sheet changed from previous item
                    const showDivider =
                      index > 0 &&
                      item.sheetKey !== tableData[index - 1].sheetKey;
                    const validationHistory = getValidationHistory(item);
                    const latestValidation = validationHistory.at(-1) ?? null;
                    const isCurrentlyValidated = Boolean(
                      latestValidation?.valid
                    );
                    const ignoreToggleLocked = Boolean(
                      ignoreToggleDisabledByFinalize ||
                        (item.isOriginal && item.ignored)
                    );

                    return (
                      <React.Fragment key={index}>
                        {/* Sheet Divider */}
                        {showDivider && (
                          <tr className="bg-gradient-to-r from-[var(--qt-accent-soft)] to-[var(--qt-accent-soft)]">
                            <td colSpan={3} className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="flex flex-1 items-center gap-3">
                                  <div className="h-px bg-gradient-to-r from-transparent via-[var(--qt-accent-line)] to-transparent flex-1"></div>
                                  <span
                                    className={clsx(
                                      'font-semibold text-[var(--qt-accent-color)] uppercase tracking-wide',
                                      bodyTextClass
                                    )}
                                  >
                                    {item.sheetKey?.replace(/_/g, ' ')}
                                  </span>
                                  <div className="h-px bg-gradient-to-r from-transparent via-[var(--qt-accent-line)] to-transparent flex-1"></div>
                                </div>
                                {!answeringRFP ? (
                                  <button
                                    onClick={() =>
                                      handleAddRowForSheet(item.sheetKey)
                                    }
                                    className="ml-auto whitespace-nowrap rounded border border-[var(--qt-accent-color)] px-2 py-1 text-xs font-medium text-[var(--qt-accent-color)] hover:bg-[var(--qt-accent-color)] hover:text-white transition-all"
                                    aria-label={`Add row to ${item.sheetKey}`}
                                    title="Add row"
                                  >
                                    Add row
                                  </button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        )}

                        {/* First item gets a divider too */}
                        {index === 0 && item.sheetKey && (
                          <tr className="bg-gradient-to-r from-[var(--qt-accent-soft)] to-[var(--qt-accent-soft)]">
                            <td colSpan={3} className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="flex flex-1 items-center gap-3">
                                  <div className="h-px bg-gradient-to-r from-transparent via-[var(--qt-accent-line)] to-transparent flex-1"></div>
                                  <span
                                    className={clsx(
                                      'font-semibold text-[var(--qt-accent-color)] uppercase tracking-wide',
                                      bodyTextClass
                                    )}
                                  >
                                    {item.sheetKey?.replace(/_/g, ' ')}
                                  </span>
                                  <div className="h-px bg-gradient-to-r from-transparent via-[var(--qt-accent-line)] to-transparent flex-1"></div>
                                </div>
                                {!answeringRFP ? (
                                  <button
                                    onClick={() =>
                                      handleAddRowForSheet(item.sheetKey)
                                    }
                                    className="ml-auto whitespace-nowrap rounded border border-[var(--qt-accent-color)] px-2 py-1 text-xs font-medium text-[var(--qt-accent-color)] hover:bg-[var(--qt-accent-color)] hover:text-white transition-all"
                                    aria-label={`Add row to ${item.sheetKey}`}
                                    title="Add row"
                                  >
                                    Add row
                                  </button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        )}

                        {/* Question Row */}
                        <tr
                          className={`transition-all duration-200 ${
                            item.ignored
                              ? 'bg-slate-50 opacity-60'
                              : 'hover:bg-[var(--qt-accent-soft)]'
                          }`}
                          tabIndex={0}
                          onFocus={() => {
                            setHighlightText(item.question);
                            setCurrentIndex(index);
                          }}
                          onClick={() => {
                            setHighlightText(item.question);
                            setCurrentIndex(index);
                          }}
                        >
                          {/* Ignore Checkbox */}
                          <td className="px-6 py-4 w-24">
                            <button
                              onClick={() => handleIgnoreToggle(index)}
                              disabled={ignoreToggleLocked}
                              className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all duration-200 ${
                                item.ignored
                                  ? 'bg-[var(--qt-accent-color)] border-[var(--qt-accent-color)]'
                                  : 'border-slate-300 hover:border-[var(--qt-accent-color)]'
                              } ${
                                ignoreToggleLocked
                                  ? 'cursor-not-allowed opacity-70'
                                  : ''
                              }`}
                            >
                              {item.ignored && (
                                <Check className="w-4 h-4 text-white" />
                              )}
                            </button>
                          </td>

                          {/* Question Cell */}
                          <td className="px-6 py-4 w-1/2">
                            {editingCell?.index === index &&
                            editingCell?.field === 'question' ? (
                              <div className="space-y-2">
                                <textarea
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onKeyDown={handleKeyDown}
                                  className="w-full px-3 py-2 border-2 border-[var(--qt-accent-color)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--qt-accent-color)] focus:border-transparent resize-none"
                                  rows={3}
                                  autoFocus
                                />
                                <div className="flex gap-2">
                                  <button
                                    onClick={handleEditSave}
                                    className="px-3 py-1 bg-[var(--qt-accent-color)] text-white rounded-md hover:brightness-95 transition-all text-sm font-medium flex items-center gap-1"
                                  >
                                    <Save className="w-4 h-4" />
                                    Save
                                  </button>
                                  <button
                                    onClick={handleEditCancel}
                                    className="px-3 py-1 bg-slate-200 text-slate-700 rounded-md hover:bg-slate-300 transition-colors text-sm font-medium"
                                  >
                                    Cancel
                                  </button>
                                  {/* <button
                                    onClick={handleHighlight}
                                    className="px-3 py-1 bg-slate-200 text-slate-700 rounded-md hover:bg-slate-300 transition-colors text-sm font-medium"
                                  >
                                    Highlight
                                  </button> */}
                                </div>
                              </div>
                            ) : (
                              <div className="group flex items-start gap-2">
                                <div className="flex-1 space-y-1">
                                  {item.userInput ? (
                                    <span className="inline-flex rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                                      User Input Required
                                    </span>
                                  ) : null}
                                  <p
                                    className={clsx(
                                      bodyTextClass,
                                      item.ignored
                                        ? 'text-slate-400'
                                        : 'text-slate-900'
                                    )}
                                  >
                                    {item.question}
                                  </p>
                                </div>
                                {!isCurrentlyValidated && !item.ignored ? (
                                  <button
                                    onClick={() =>
                                      handleEditStart(
                                        index,
                                        'question',
                                        item.question
                                      )
                                    }
                                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-slate-100 rounded"
                                  >
                                    <Edit2 className="w-4 h-4 text-slate-400 hover:text-[var(--qt-accent-color)]" />
                                  </button>
                                ) : null}
                              </div>
                            )}
                          </td>

                          {/* Answer Cell */}
                          <td className="px-6 py-4">
                            {editingCell?.index === index &&
                            editingCell?.field === 'answer' ? (
                              <div className="space-y-2">
                                <textarea
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onKeyDown={handleKeyDown}
                                  className="w-full px-3 py-2 border-2 border-[var(--qt-accent-color)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--qt-accent-color)] focus:border-transparent resize-none"
                                  rows={3}
                                  autoFocus
                                  placeholder="Enter answer..."
                                />
                                <div className="flex gap-2">
                                  <button
                                    onClick={handleEditSave}
                                    className="px-3 py-1 bg-[var(--qt-accent-color)] text-white rounded-md hover:brightness-95 transition-all text-sm font-medium flex items-center gap-1"
                                  >
                                    <Save className="w-4 h-4" />
                                    Save
                                  </button>
                                  <button
                                    onClick={handleEditCancel}
                                    className="px-3 py-1 bg-slate-200 text-slate-700 rounded-md hover:bg-slate-300 transition-colors text-sm font-medium"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div className="group flex items-start gap-2">
                                  <p
                                    className={clsx(
                                      'flex-1',
                                      bodyTextClass,
                                      item.ignored
                                        ? 'text-slate-400'
                                        : item.answer
                                          ? 'text-slate-900'
                                          : 'text-slate-400 italic'
                                    )}
                                  >
                                    {item.answer || 'No answer provided'}
                                  </p>
                                  {!isCurrentlyValidated && !item.ignored ? (
                                    <button
                                      onClick={() =>
                                        handleEditStart(
                                          index,
                                          'answer',
                                          item.answer
                                        )
                                      }
                                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-slate-100 rounded"
                                    >
                                      <Edit2 className="w-4 h-4 text-slate-400 hover:text-[var(--qt-accent-color)]" />
                                    </button>
                                  ) : null}
                                </div>
                                {answeringRFP &&
                                  !item.ignored &&
                                  isCurrentlyValidated && (
                                    <div className="w-full flex justify-end">
                                      <button
                                        className="h-8 w-8 rounded-md bg-slate-200 text-slate-700 transition-all hover:bg-slate-300 flex items-center justify-center"
                                        onClick={() =>
                                          openValidationDetails(index)
                                        }
                                        aria-label="Validation details"
                                      >
                                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500">
                                          <Check className="h-3 w-3 text-white" />
                                        </span>
                                      </button>
                                    </div>
                                  )}
                              </div>
                            )}
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer Info */}
          <div className="mt-6 flex items-center justify-between text-sm text-slate-600">
            <p>
              Total Questions:{' '}
              <span className="font-semibold text-slate-900">
                {tableData.length}
              </span>
            </p>
            <p>
              Ignored:{' '}
              <span className="font-semibold text-slate-900">
                {tableData.filter((item) => item.ignored).length}
              </span>
            </p>
          </div>
        </div>
      </div>
      {showRfpAnswerPanel ? (
        <div className="h-full min-h-0 w-[30%] min-w-[30%] rounded-xl border border-slate-200/80 bg-gradient-to-br from-slate-50 to-white p-3 shadow-sm">
          <div className="h-full min-h-0">
            {selectedRfpPanelQuestion ? (
              <RFPAnswerCard
                data={selectedRfpPanelQuestion}
                acceptAnswer={handlePanelAcceptAnswer}
                validateAnswer={handlePanelValidateAnswer}
                currentCell={selectedRfpPanelCell}
                handleUpdateFromEdit={handlePanelUpdateFromEdit}
                highLightNonValidatedCells={
                  handlePanelHighlightNonValidatedCells
                }
                origin="QuestionnaireTable"
                finalized={Boolean(documentRecord?.finalized)}
              />
            ) : (
              <div className="rounded-lg border border-slate-200 bg-white px-3 py-4 text-sm text-slate-600">
                Select a question row to review the full RFP answer card.
              </div>
            )}
          </div>
        </div>
      ) : null}
      {validationDetails.visible && validationDetails.entry ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4"
          onClick={closeValidationDetails}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">
                Validation Details
              </h2>
              <button
                onClick={closeValidationDetails}
                className="rounded-md p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
                aria-label="Close validation details"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-2 text-sm text-slate-700">
              <p>
                Validated by:{' '}
                <span className="font-semibold text-slate-900">
                  {validationDetails.entry.name}
                </span>
              </p>
              <p>
                Date:{' '}
                <span className="font-semibold text-slate-900">
                  {formatValidationDate(validationDetails.entry.date)}
                </span>
              </p>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={closeValidationDetails}
                className="rounded-md bg-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-300"
              >
                Close
              </button>
              <button
                onClick={handleUnvalidate}
                className="rounded-md bg-slate-700 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-slate-800"
              >
                Unvalidate
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {showHelpModal ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4"
          onClick={() => setShowHelpModal(false)}
        >
          <div
            className="w-full max-w-2xl rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Help</h2>
              <button
                onClick={() => setShowHelpModal(false)}
                className="rounded-md p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
                aria-label="Close help modal"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <ol className="space-y-3 text-sm text-slate-700">
              <li>
                <span className="font-semibold text-slate-900">
                  1. User Input Required:
                </span>{' '}
                This is not going to be answered by the system.
              </li>
              <li>
                <span className="font-semibold text-slate-900">2. Ignore:</span>{' '}
                These questions will simply be ignored.
              </li>
              <li>
                <span className="font-semibold text-slate-900">
                  3. Add Row:
                </span>{' '}
                This will allow the user to add a "question" to the selected
                page.
              </li>
            </ol>
          </div>
        </div>
      ) : null}
      <Modal
        visible={showDocumentSelector}
        onDismiss={() => setShowDocumentSelector(false)}
        size="large"
        footer={
          <div className="flex w-full gap-4 justify-end">
            <Button onClick={() => handleCancel()}>Cancel</Button>
            <Button onClick={() => handleUploadQuestionsForGeneration()}>
              Generate Answers
            </Button>
          </div>
        }
      >
        <div>
          <DocumentFilter
            KBChecked={KBChecked}
            setKBChecked={setKBChecked}
            kbFilters={kbFilters}
            setKBFilters={setKBFilters}
            prevChecked={prevChecked}
            setPrevChecked={setPrevChecked}
            selectedDocuments={selectedDocuments}
            setSelectedDocuments={setSelectedDocuments}
            similarityThreshold={similarityThreshold}
            setSimilarThreshold={setSimilarThreshold}
            type={'rfp'}
          />
        </div>
      </Modal>
      {/* <Modal
        visible={showCoinsModal.visible}
        onDismiss={() => setShowCoinsModal({ visible: false, type: '' })}
      >
        <>
          {showCoinsModal.type === 'BuyMore' ? (
            <div>
              <p className="text-sm text-gray-700 mb-4">
                You do not have enough Compliance Credits to perform this
                document analysis. To continue using advanced features like
                document analysis and AI answers, additional credits are
                required.
              </p>
              {user?.isAdmin ? (
                <Button
                  onClick={() => {
                    setShowCoinsModal({ visible: false, type: '' });
                    setSubsAlert('', 'coins');
                  }}
                  // className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded"
                >
                  Buy More Credits
                </Button>
              ) : (
                <button
                  className="bg-gray-400 text-white font-medium py-2 px-4 rounded cursor-not-allowed"
                  disabled
                >
                  Contact your administrator to purchase more credits
                </button>
              )}
            </div>
          ) : null}
        </>
      </Modal> */}
    </div>
  );
};

export default QuestionnaireTable;
